import { useEffect, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, Wallet, Check, XCircle, Clock, User, Phone, AlertCircle, Filter, Banknote, CreditCard, Building2, Copy } from "lucide-react";

const STATUS_META = {
  pending:  { label: "قيد المراجعة", cls: "bg-yellow-100 text-yellow-700", icon: Clock },
  approved: { label: "معتمد",        cls: "bg-green-100 text-green-700",   icon: Check },
  rejected: { label: "مرفوض",        cls: "bg-red-100 text-red-700",       icon: XCircle },
};

const METHOD_META = {
  cash:  { label: "نقداً",       icon: Banknote,   cls: "bg-emerald-100 text-emerald-700" },
  mypay: { label: "MyPay",       icon: CreditCard, cls: "bg-purple-100 text-purple-700" },
  bank:  { label: "تحويل مصرفي", icon: Building2,  cls: "bg-blue-100 text-blue-700" },
};

function PayoutDetails({ req }) {
  const method = req.payout_method || "cash";
  const d = req.payout_details || {};
  const M = METHOD_META[method] || METHOD_META.cash;
  const copy = (v) => { try { navigator.clipboard.writeText(v); } catch {/* noop */} };
  return (
    <div className={`mt-2 rounded-xl p-2.5 ${M.cls} border border-current/10`}>
      <div className="flex items-center gap-1.5 text-xs font-black">
        <M.icon size={13}/> {M.label}
      </div>
      {method === "mypay" && d.mypay_phone && (
        <div className="flex items-center justify-between mt-1 text-xs">
          <span>رقم MyPay: <b className="font-mono">{d.mypay_phone}</b></span>
          <button onClick={() => copy(d.mypay_phone)} className="p-1 hover:bg-white/50 rounded" title="نسخ"><Copy size={11}/></button>
        </div>
      )}
      {method === "bank" && (
        <div className="mt-1 space-y-0.5 text-xs">
          {d.bank_name && <div>المصرف: <b>{d.bank_name}</b></div>}
          {d.bank_account && (
            <div className="flex items-center justify-between">
              <span>الحساب: <b className="font-mono">{d.bank_account}</b></span>
              <button onClick={() => copy(d.bank_account)} className="p-1 hover:bg-white/50 rounded" title="نسخ"><Copy size={11}/></button>
            </div>
          )}
          {d.bank_holder && <div>صاحب الحساب: <b>{d.bank_holder}</b></div>}
        </div>
      )}
    </div>
  );
}

/**
 * Admin panel for reviewing driver wallet cashout requests.
 * Filter by status, approve/reject with optional reason.
 */
export default function AdminCashoutsModal({ onClose, onChange }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cashouts${status ? `?status=${status}` : ""}`);
      setItems(data.items || []);
    } catch (e) { setErr(extractError(e) || "تعذّر تحميل الطلبات"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const approve = async (id) => {
    if (!window.confirm("تأكيد اعتماد طلب السحب هذا؟")) return;
    setBusyId(id); setErr("");
    try {
      await api.post(`/admin/cashouts/${id}/approve`);
      await load(); onChange && onChange();
    } catch (e) { setErr(extractError(e) || "تعذّر الاعتماد"); }
    finally { setBusyId(""); }
  };

  const reject = async (id) => {
    const reason = window.prompt("سبب الرفض (اختياري):", "");
    if (reason === null) return;
    setBusyId(id); setErr("");
    try {
      await api.post(`/admin/cashouts/${id}/reject`, { reason: reason.trim() || undefined });
      await load(); onChange && onChange();
    } catch (e) { setErr(extractError(e) || "تعذّر الرفض"); }
    finally { setBusyId(""); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="admin-cashouts-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col" style={{maxHeight:"92vh"}}>
        <div className="flex justify-between items-center p-4 border-b border-[var(--border)] bg-gradient-to-l from-primary-brand to-[#1E3AB0] text-white">
          <div className="flex items-center gap-2"><Wallet size={18}/><h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>طلبات سحب السائقين</h2></div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="admin-cashouts-close"><X size={16}/></button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-1 p-3 border-b border-[var(--border)] bg-[var(--surface-alt)] overflow-x-auto">
          <Filter size={14} className="text-[var(--text-secondary)] shrink-0"/>
          {[
            { v: "pending", label: "قيد المراجعة" },
            { v: "approved", label: "معتمدة" },
            { v: "rejected", label: "مرفوضة" },
            { v: "", label: "الكل" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setStatus(f.v)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap ${status === f.v ? "bg-primary-brand text-white" : "bg-white text-[var(--text-secondary)] border border-[var(--border)]"}`}
              data-testid={`cashouts-filter-${f.v || "all"}`}
            >{f.label}</button>
          ))}
        </div>

        <div className="flex-1 overflow-auto p-4">
          {err && (
            <div className="flex items-start gap-2 p-3 mb-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertCircle size={16} className="shrink-0 mt-0.5"/> {err}
            </div>
          )}
          {loading ? (
            <div className="text-center text-sm text-[var(--text-secondary)] py-10">جارٍ التحميل...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-10">
              <Wallet size={32} className="mx-auto opacity-40 mb-2"/>
              <div className="text-sm text-[var(--text-secondary)]">لا يوجد طلبات</div>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((r) => {
                const M = STATUS_META[r.status] || STATUS_META.pending;
                const isPending = r.status === "pending";
                return (
                  <div key={r.id} className="bg-white rounded-2xl border border-[var(--border)] p-3" data-testid={`ac-item-${r.id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-black text-lg text-primary-brand" style={{fontFamily:'Cairo'}}>{r.amount.toFixed(2)} د.ل</span>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-full ${M.cls}`}>
                            <M.icon size={10}/> {M.label}
                          </span>
                        </div>
                        <div className="mt-1 text-sm font-bold flex items-center gap-1"><User size={12}/> {r.driver_name || "—"}</div>
                        {r.driver_phone && (
                          <a href={`tel:${r.driver_phone}`} className="text-xs text-primary-brand flex items-center gap-1 hover:underline">
                            <Phone size={10}/> {r.driver_phone}
                          </a>
                        )}
                        <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                          {new Date(r.requested_at).toLocaleString("ar-LY", { dateStyle: "short", timeStyle: "short" })}
                        </div>
                        {r.note && <div className="text-xs text-[var(--text-secondary)] mt-1">📝 {r.note}</div>}
                        <PayoutDetails req={r} />
                        {r.reject_reason && (
                          <div className="text-xs text-red-600 mt-2 bg-red-50 px-2 py-1 rounded">
                            سبب الرفض: {r.reject_reason}
                          </div>
                        )}
                        {r.admin_name && !isPending && (
                          <div className="text-[10px] text-[var(--text-secondary)] mt-1">
                            بواسطة: {r.admin_name}
                          </div>
                        )}
                      </div>
                      {isPending && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            onClick={() => approve(r.id)}
                            disabled={busyId === r.id}
                            className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-black flex items-center gap-1 hover:bg-green-700 disabled:opacity-60"
                            data-testid={`ac-approve-${r.id}`}
                          >
                            <Check size={12}/> اعتماد
                          </button>
                          <button
                            onClick={() => reject(r.id)}
                            disabled={busyId === r.id}
                            className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-xs font-black flex items-center gap-1 hover:bg-red-200 disabled:opacity-60"
                            data-testid={`ac-reject-${r.id}`}
                          >
                            <XCircle size={12}/> رفض
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
